import { useEffect, useState } from "react";
import { Briefcase } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  ROLE_CATEGORIES,
  findCategoryForTitle,
} from "../constants/roles";

/**
 * Grouped role dropdown + optional free-text "Other" input.
 *
 * Props:
 *   value: { role_title: string, role_other: string }
 *   onChange: ({ role_category, role_title, role_other }) => void
 *   labelText: optional override label
 *   required: boolean
 *   testIdPrefix: string prefix for data-testid attrs (default "role")
 */
const RoleSelect = ({
  value = { role_title: "", role_other: "" },
  onChange,
  labelText = "I work in healthcare as a…",
  required = false,
  testIdPrefix = "role",
}) => {
  const [title, setTitle] = useState(value.role_title || "");
  const [otherText, setOtherText] = useState(value.role_other || "");

  useEffect(() => {
    setTitle(value.role_title || "");
    setOtherText(value.role_other || "");
  }, [value.role_title, value.role_other]);

  const propagate = (nextTitle, nextOther) => {
    const role_category = nextTitle ? findCategoryForTitle(nextTitle) : "";
    onChange?.({
      role_category,
      role_title: nextTitle,
      role_other: nextTitle === "Other" ? nextOther : "",
    });
  };

  const handleTitleChange = (e) => {
    const next = e.target.value;
    setTitle(next);
    propagate(next, otherText);
  };

  const handleOtherChange = (e) => {
    const next = e.target.value;
    setOtherText(next);
    propagate(title, next);
  };

  const isOther = title === "Other";

  return (
    <div data-testid={`${testIdPrefix}-select-wrapper`}>
      <Label htmlFor={`${testIdPrefix}-select`} className="text-slate-700">
        {labelText}
      </Label>
      <div className="relative mt-1">
        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
        <select
          id={`${testIdPrefix}-select`}
          value={title}
          onChange={handleTitleChange}
          required={required}
          data-testid={`${testIdPrefix}-select`}
          className="w-full h-12 pl-10 pr-3 border border-slate-200 rounded-md bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary appearance-none"
        >
          <option value="">— Select your role —</option>
          {ROLE_CATEGORIES.map((cat) => (
            <optgroup key={cat.key} label={cat.label}>
              {cat.titles.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      {isOther && (
        <div className="mt-3">
          <Label htmlFor={`${testIdPrefix}-other`} className="text-slate-700 text-sm">
            Please specify your role
          </Label>
          <Input
            id={`${testIdPrefix}-other`}
            type="text"
            value={otherText}
            onChange={handleOtherChange}
            placeholder="e.g. Clinical Research Coordinator"
            className="mt-1 h-11"
            maxLength={80}
            data-testid={`${testIdPrefix}-other-input`}
          />
        </div>
      )}
    </div>
  );
};

export default RoleSelect;
